/**
 * Cart drawer product suggestion (story 03).
 *
 * Picks add-ons for the item a customer just added and shows them as a full-drawer
 * takeover, once per the frequency setting. Outside that window nothing renders.
 * See snippets/cart-suggestion.liquid for the data and the precedence rules.
 *
 * The drawer re-renders its whole section on every cart change, so this element
 * is destroyed and rebuilt constantly. Anything that has to outlive a re-render
 * (which item was added, whether the takeover is open, which products it chose)
 * lives in window.PrimalSuggestion, not on the instance.
 */

// Merged, not replaced: cart-drawer.js writes lastAdded here and either script
// may run first.
window.PrimalSuggestion = Object.assign(
  {
    lastAdded: null, // {productId, variantId, title} set by cart-drawer.js on add
    open: false, // takeover currently showing
    candidateIds: null, // frozen list, so the takeover doesn't reshuffle under the user
    basisId: null, // product the takeover was built from
  },
  window.PrimalSuggestion || {}
);

// proof.pair is only trustworthy with enough buyers behind it and a share worth
// putting a sentence on. Same thresholds the story specifies.
const PAIR_MIN_BUYERS = 30;
const PAIR_MIN_SHARE = 0.15;
const TAKEOVER_KEY = "primal:suggestion:lastTakeover";
const DAY_MS = 86400000;

const SOAP_TYPES = ["Soap Bar", "Shampoo Bar"];
const EXFOLIATION_TYPES = ["Exfoliation"];
const LOOFAH_HANDLES = [
  "natural-egyptian-loofah-sponge",
  "natural-egyptian-loofah-pad",
];

class CartSuggestion extends HTMLElement {
  connectedCallback() {
    try {
      this.data = JSON.parse(
        this.querySelector(".cart-suggestion__data").textContent
      );
    } catch (e) {
      return; // malformed payload: render nothing rather than a broken card
    }

    window.PrimalSuggestion.moneyFormat = this.dataset.moneyFormat || "${{amount}}";
    this.byId = new Map(this.data.products.map((p) => [p.id, p]));
    this.cartIds = new Set(this.data.cartItems.map((i) => i.productId));
    CartSuggestion.recCache.forEach((list) =>
      list.forEach((p) => {
        if (!this.byId.has(p.id)) this.byId.set(p.id, p);
      })
    );

    // The complementary picks normally arrive in the payload. They won't if the
    // app-owned metafield isn't exposed to the storefront, so fall back to the
    // recommendations endpoint once per product and cache it for the session.
    const basis = this.basis;
    if (
      basis &&
      !basis.complementIds.length &&
      !CartSuggestion.recCache.has(basis.productId)
    ) {
      this.fetchComplements(basis.productId).then(() => {
        CartSuggestion.recCache
          .get(basis.productId)
          .forEach((p) => this.byId.set(p.id, p));
        this.render();
      });
      return;
    }

    this.render();
  }

  fetchComplements(productId) {
    const url = `/recommendations/products.json?product_id=${productId}&intent=complementary&limit=6`;
    return fetch(url)
      .then((r) => r.json())
      .then((json) => {
        CartSuggestion.recCache.set(
          productId,
          (json.products || []).map(normalizeProduct)
        );
      })
      .catch(() => CartSuggestion.recCache.set(productId, []));
  }

  /** Complement ids for a cart item: from Liquid when present, else the fetched list. */
  complementIdsFor(basis) {
    if (basis.complementIds.length) return basis.complementIds;
    return (CartSuggestion.recCache.get(basis.productId) || []).map((p) => p.id);
  }

  /** The item the suggestion is built around: what was just added, else the first line. */
  get basis() {
    const state = window.PrimalSuggestion;
    const added =
      state.lastAdded &&
      this.data.cartItems.find((i) => i.productId === state.lastAdded.productId);
    return added || this.data.cartItems[0] || null;
  }

  /**
   * Candidates in precedence order. `keepInCart` is true for the takeover, which
   * marks already-owned rows "Added" instead of dropping them mid-interaction.
   */
  candidatesFor(basis, keepInCart) {
    if (!basis) return [];

    const usable = (id) => {
      const p = this.byId.get(id);
      if (!p || !p.available || !p.variantId) return false;
      if (id === basis.productId) return false;
      if (!keepInCart && this.cartIds.has(id)) return false;
      return true;
    };

    // 1. complementary products, in the order Search & Discovery has them
    const out = this.complementIdsFor(basis).filter(usable);

    // 2. the proof pair, when the numbers clear and it isn't already listed
    const pairId = this.pairIdOf(basis);
    if (pairId && usable(pairId) && !out.includes(pairId)) out.push(pairId);

    // 3. a loofah, but only for a shower cart that hasn't got one
    if (!out.length && this.wantsLoofah()) {
      const loofah = LOOFAH_HANDLES.map((h) =>
        this.data.products.find((p) => p.handle === h)
      ).find((p) => p && usable(p.id));
      if (loofah) out.push(loofah.id);
    }

    return out;
  }

  /** Product id of basis's co-purchase pair, or null when the data is too thin to quote. */
  pairIdOf(basis) {
    if (!basis || !basis.pairId) return null;
    if (basis.buyers < PAIR_MIN_BUYERS || basis.pairShare < PAIR_MIN_SHARE) {
      return null;
    }
    const numeric = Number(String(basis.pairId).split("/").pop());
    return Number.isFinite(numeric) ? numeric : null;
  }

  wantsLoofah() {
    const types = this.data.cartItems
      .map((i) => this.byId.get(i.productId))
      .filter(Boolean)
      .map((p) => p.type);
    return (
      types.some((t) => SOAP_TYPES.includes(t)) &&
      !types.some((t) => EXFOLIATION_TYPES.includes(t))
    );
  }

  /**
   * A row only gets a line when there's a real number behind it. The generic
   * "goes with your X" belongs in the heading, said once, not on every row.
   * The sentence avoids naming either product: both titles are already on screen.
   */
  lineFor(candidate, basis) {
    if (this.pairIdOf(basis) !== candidate.id) return "";
    return `1 in ${Math.ceil(1 / basis.pairShare)} buy these together.`;
  }

  shouldTakeover() {
    const state = window.PrimalSuggestion;
    if (!state.lastAdded) return false; // drawer opened from the cart icon, not an add

    const mode = this.dataset.frequency;
    if (mode === "always") return true;
    try {
      if (mode === "tab") return !sessionStorage.getItem(TAKEOVER_KEY);
      const last = parseInt(localStorage.getItem(TAKEOVER_KEY) || "0", 10);
      return Date.now() - last > DAY_MS;
    } catch (e) {
      return true; // storage blocked (private mode): show it rather than never
    }
  }

  markTakeoverShown() {
    try {
      if (this.dataset.frequency === "tab") {
        sessionStorage.setItem(TAKEOVER_KEY, "1");
      } else {
        localStorage.setItem(TAKEOVER_KEY, String(Date.now()));
      }
    } catch (e) {
      /* storage blocked: the takeover just shows again next time */
    }
  }

  render() {
    const state = window.PrimalSuggestion;
    const basis = this.basis;
    if (!basis) return this.hide();

    // A takeover already on screen keeps its original list and basis, so adding
    // from it doesn't swap the rows the customer is reading.
    if (state.open && state.candidateIds) {
      const basisItem =
        this.data.cartItems.find((i) => i.productId === state.basisId) || basis;
      const kept = state.candidateIds
        .map((id) => this.byId.get(id))
        .filter(Boolean);
      if (kept.length) return this.renderTakeover(kept, basisItem);
    }

    if (!this.shouldTakeover()) return this.hide();

    const max = parseInt(this.dataset.maxCards, 10) || 3;
    const min = parseInt(this.dataset.minTakeover, 10) || 2;
    const ids = this.candidatesFor(basis, true)
      .filter((id) => !this.cartIds.has(id))
      .slice(0, max);
    if (ids.length < min) return this.hide();

    state.open = true;
    state.candidateIds = ids;
    state.basisId = basis.productId;
    this.markTakeoverShown();
    return this.renderTakeover(
      ids.map((id) => this.byId.get(id)),
      basis
    );
  }

  hide() {
    this.hidden = true;
    this.dataset.mode = "";
    this.replaceChildren(...this.dataChildren());
  }

  /** Keep the JSON payload in the DOM when swapping rendered content. */
  dataChildren() {
    const json = this.querySelector(".cart-suggestion__data");
    return json ? [json] : [];
  }

  renderTakeover(products, basis) {
    const rows = products
      .map((p) => this.rowHTML(p, basis, this.cartIds.has(p.id)))
      .join("");

    this.dataset.mode = "takeover";
    this.hidden = false;
    this.replaceChildren(...this.dataChildren());
    this.insertAdjacentHTML(
      "beforeend",
      `<div class="cart-suggestion__panel">
         ${this.headHTML(basis)}
         <div class="cart-suggestion__group">
           <p class="cart-suggestion__heading">Goes with your ${esc(shortTitle(basis.title))}</p>
           <ul class="cart-suggestion__list list-unstyled" role="list">${rows}</ul>
         </div>
         <div class="cart-suggestion__foot">
           <button type="button" class="cart-suggestion__skip button button--secondary" data-skip>
             View cart${this.data.cartTotal ? ` &middot; ${esc(this.data.cartTotal)}` : ""}
           </button>
         </div>
       </div>`
    );

    this.bind();
    products.forEach((p) => this.track("drawer_suggestion_shown", p));
  }

  /** "Added" confirmation, built like a row so it reads as part of the same list. */
  headHTML(basis) {
    const product = this.byId.get(basis.productId);
    const image =
      product && product.image
        ? `<img src="${esc(product.image)}" alt="" width="64" height="64">`
        : "";
    return `<div class="cart-suggestion__head">
      <span class="cart-suggestion__head-media">
        ${image}
        <span class="cart-suggestion__check" aria-hidden="true">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3"
               stroke-linecap="round" stroke-linejoin="round"><path d="M20 6 9 17l-5-5"/></svg>
        </span>
      </span>
      <span class="cart-suggestion__head-text">
        <span class="cart-suggestion__added">Added to your cart</span>
        <span class="cart-suggestion__title">${esc(basis.title)}</span>
        <button type="button" class="cart-suggestion__dismiss" data-skip>Skip to cart</button>
      </span>
    </div>`;
  }

  rowHTML(product, basis, added) {
    const rating =
      product.rating && product.reviewCount
        ? `<span class="cart-suggestion__rating">
             <span class="cart-suggestion__stars" aria-hidden="true">&#9733;</span>
             ${product.rating.toFixed(1)}
           </span>`
        : "";

    const button = added
      ? `<span class="cart-suggestion__added-tag">Added</span>`
      : `<button type="button" class="cart-suggestion__add button button--secondary"
                 data-variant-id="${product.variantId}" data-product-id="${product.id}">
           <span class="cart-suggestion__add-label">Add</span>
           <span class="cart-suggestion__spinner" aria-hidden="true"></span>
         </button>`;

    return `<li class="cart-suggestion__row${added ? " is-added" : ""}">
      <a class="cart-suggestion__media" href="${esc(product.url)}" tabindex="-1" aria-hidden="true">
        ${
          product.image
            ? `<img src="${esc(product.image)}" alt="" width="64" height="64" loading="lazy">`
            : ""
        }
      </a>
      <div class="cart-suggestion__info">
        <a class="cart-suggestion__name" href="${esc(product.url)}">${esc(product.title)}</a>
        ${
          this.lineFor(product, basis)
            ? `<p class="cart-suggestion__line">${esc(this.lineFor(product, basis))}</p>`
            : ""
        }
        ${rating}
      </div>
      <div class="cart-suggestion__action">
        <span class="cart-suggestion__price">${esc(product.priceFormatted)}</span>
        ${button}
      </div>
    </li>`;
  }

  bind() {
    this.querySelectorAll("[data-variant-id]").forEach((btn) =>
      btn.addEventListener("click", this.onAdd.bind(this))
    );
    this.querySelectorAll("[data-skip]").forEach((el) =>
      el.addEventListener("click", this.onSkip.bind(this))
    );

  }

  onSkip() {
    (window.PrimalSuggestion.candidateIds || []).forEach((id) => {
      const p = this.byId.get(id);
      if (p && !this.cartIds.has(id)) this.track("drawer_suggestion_skipped", p);
    });
    window.PrimalSuggestion.open = false;
    window.PrimalSuggestion.candidateIds = null;
    window.PrimalSuggestion.lastAdded = null;
    this.render();
  }

  onAdd(event) {
    const button = event.currentTarget;
    const product = this.byId.get(Number(button.dataset.productId));
    submitAdd(button, () => {
      if (product) this.track("drawer_suggestion_added", product);
    });
  }

  track(event, product) {
    try {
      window.Shopify.analytics.publish(event, {
        product: product.handle,
        productId: product.id,
      });
    } catch (e) {
      /* analytics unavailable: never block the UI for it */
    }
  }
}

/**
 * Add one variant through the drawer's own path so line items, totals, progress
 * bar and cart bubble all update together. Shared by the takeover rows and the
 * empty-cart picks. `onSuccess` runs once the add is confirmed, before the drawer
 * re-renders (which destroys the button that was clicked).
 */
function submitAdd(button, onSuccess) {
  if (button.hasAttribute("aria-disabled")) return;
  button.setAttribute("aria-disabled", "true");
  button.classList.add("loading");

  const drawer = document.querySelector("cart-drawer");
  const body = new FormData();
  body.append("id", button.dataset.variantId);
  body.append("quantity", 1);
  if (drawer) {
    body.append("sections", drawer.getSectionsToRender().map((s) => s.id));
    body.append("sections_url", window.location.pathname);
  }
  const startedAt = Date.now();

  fetch(window.routes.cart_add_url, {
    method: "POST",
    headers: { "X-Requested-With": "XMLHttpRequest", Accept: "application/javascript" },
    body,
  })
    .then((response) => response.json())
    .then((parsed) => {
      if (parsed.status) throw new Error(parsed.description || parsed.message);
      if (onSuccess) onSuccess(parsed);
      // CartItems subscribes to this and it is what drives the progress bar
      // forward; product-form.js publishes the same event before re-rendering.
      if (typeof publish === "function" && typeof PUB_SUB_EVENTS !== "undefined") {
        publish(PUB_SUB_EVENTS.cartUpdate, {
          source: "cart-suggestion",
          productVariantId: button.dataset.variantId,
          cartData: parsed,
        });
      }
      // Floor of 350ms so the spinner is actually seen: a local add resolves in
      // well under that and the button would just blink.
      const wait = Math.max(0, 350 - (Date.now() - startedAt));
      setTimeout(() => {
        if (!drawer) return;
        // product-form.js does this in its finally(); without it the drawer keeps
        // Dawn's empty-state layout (header hidden, centred grid) after the first
        // add from the empty-cart picks.
        drawer.classList.remove("is-empty");
        drawer.renderContents(parsed);
      }, wait);
    })
    .catch(() => {
      button.removeAttribute("aria-disabled");
      button.classList.remove("loading");
    });
}

/**
 * Empty-cart bestsellers (snippets/cart-empty-picks.liquid). Liquid renders the
 * rows; this only wires the Add buttons and analytics.
 */
class CartEmptyPicks extends HTMLElement {
  connectedCallback() {
    this.querySelectorAll("[data-variant-id]").forEach((button) => {
      trackEvent("drawer_empty_pick_shown", button.dataset);
      button.addEventListener("click", () =>
        submitAdd(button, () => trackEvent("drawer_empty_pick_added", button.dataset))
      );
    });
  }
}

function trackEvent(event, dataset) {
  try {
    window.Shopify.analytics.publish(event, {
      product: dataset.handle,
      productId: Number(dataset.productId),
    });
  } catch (e) {
    /* analytics unavailable: never block the UI for it */
  }
}

/** Endpoint products carry different key names than the Liquid payload. */
function normalizeProduct(p) {
  const variant = (p.variants || []).find((v) => v.available) || p.variants[0];
  return {
    id: p.id,
    handle: p.handle,
    title: p.title,
    type: p.type || "",
    url: p.url,
    price: p.price,
    priceFormatted: formatMoney(p.price),
    variantId: variant ? variant.id : 0,
    available: Boolean(p.available),
    image: p.featured_image ? `${p.featured_image}&width=180` : "",
    rating: 0, // metafields aren't in this payload; the row just omits the stars
    reviewCount: 0,
  };
}

/** Mirrors the shop's money_format closely enough for a price chip. */
function formatMoney(cents) {
  const amount = (Number(cents || 0) / 100).toFixed(2);
  // money_format can carry markup (<span class=money>...</span>); this string is
  // escaped before it reaches the DOM, so strip the tags rather than show them.
  const format = (window.PrimalSuggestion.moneyFormat || "${{amount}}").replace(
    /<[^>]*>/g,
    ""
  );
  return format.replace(/\{\{\s*\w+\s*\}\}/, amount).trim();
}

/** "Lavender Whipped Tallow Balm (Face & Body)" -> "Lavender Whipped Tallow Balm" */
function shortTitle(title) {
  return String(title || "").replace(/\s*\([^)]*\)\s*$/, "");
}

function esc(value) {
  return String(value == null ? "" : value).replace(
    /[&<>"']/g,
    (c) =>
      ({
        "&": "&amp;",
        "<": "&lt;",
        ">": "&gt;",
        '"': "&quot;",
        "'": "&#39;",
      }[c])
  );
}

CartSuggestion.recCache = new Map(); // productId -> normalized complements

if (!customElements.get("cart-suggestion")) {
  customElements.define("cart-suggestion", CartSuggestion);
}
if (!customElements.get("cart-empty-picks")) {
  customElements.define("cart-empty-picks", CartEmptyPicks);
}
